"use client";

import React, { createContext, useContext, useCallback } from "react";

interface Account {
  id: string;
  name: string;
  type: string;
  balance: number;
  institution_name?: string | null;
  sync_status?: string;
  is_active: boolean;
  status?: 'active' | 'closed' | 'archived';
  closed_date?: string | null;
  closure_reason?: string | null;
  closure_balance?: number | null;
}

interface AccountRefreshContextType {
  triggerAccountRefresh: () => void;
  triggerAccountRefreshWithData: (accounts: Account[]) => void;
  subscribeToRefresh: (callback: (accounts?: Account[]) => void) => () => void;
  isFetching: boolean;
  setIsFetching: (fetching: boolean) => void;
  lastFetchTime: number;
  setLastFetchTime: (time: number) => void;
}

const AccountRefreshContext = createContext<AccountRefreshContextType | undefined>(undefined);

export function AccountRefreshProvider({ children }: { children: React.ReactNode }) {
  const listenersRef = React.useRef<Set<(accounts?: Account[]) => void>>(new Set());
  const [isFetching, setIsFetching] = React.useState(false);
  const [lastFetchTime, setLastFetchTime] = React.useState(0);

  const triggerAccountRefresh = useCallback(() => {
    // Only trigger if not already fetching and enough time has passed
    const now = Date.now();
    if (!isFetching && now - lastFetchTime > 1000) { // 1 second cooldown
      setLastFetchTime(now);
      listenersRef.current.forEach(callback => callback());
    }
  }, [isFetching, lastFetchTime]);

  const triggerAccountRefreshWithData = useCallback((accounts: Account[]) => {
    setLastFetchTime(Date.now());
    listenersRef.current.forEach(callback => callback(accounts));
  }, []);

  const subscribeToRefresh = useCallback((callback: (accounts?: Account[]) => void) => {
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  return (
    <AccountRefreshContext.Provider value={{ 
      triggerAccountRefresh, 
      triggerAccountRefreshWithData, 
      subscribeToRefresh,
      isFetching,
      setIsFetching,
      lastFetchTime,
      setLastFetchTime
    }}>
      {children}
    </AccountRefreshContext.Provider>
  );
}

export function useAccountRefresh() {
  const context = useContext(AccountRefreshContext);
  if (!context) {
    throw new Error("useAccountRefresh must be used within AccountRefreshProvider");
  }
  return context;
}